import { getToken } from './role';
import { refreshSession } from './authRefresh';

// Global fetch interceptor — attaches the bearer token to SAME-ORIGIN /api requests.
//
// Many pages (Ecosystem graph, Portfolio, Risk Kernel, …) fetch entitlement-gated endpoints without
// setting an Authorization header. Server-side those endpoints are deny-by-default (RequireEntitlement
// ignores open-access), so an unauthenticated GET 402s even for a paying subscriber — the page's route
// gate (ProGate) passes on the /api/auth/me entitlement, but the page then shows no data. Rather than
// thread authHeaders() through every fetch in every page and child component, we attach the token once,
// centrally, to same-origin /api calls only.
//
// Safety: only requests whose path is /api/… on THIS origin get the header (production uses relative
// '/api/...' URLs; getApiBase() returns '' → same origin). Cross-origin requests are never touched, so
// the token cannot leak to an external host. Login/refresh are excluded. An existing Authorization
// header is never overwritten (authHeaders() callers keep their own). Any error falls back to a plain
// fetch — the interceptor can never break fetch.
export function installAuthFetch(): void {
  if (typeof window === 'undefined') return;
  const w = window as unknown as { __authFetchInstalled?: boolean; fetch: typeof fetch };
  if (w.__authFetchInstalled) return;
  w.__authFetchInstalled = true;

  const orig = window.fetch.bind(window);

  const sameOriginApi = (url: string): boolean => {
    if (!url) return false;
    if (url.startsWith('/api/')) return true; // relative, same origin
    try {
      const u = new URL(url, window.location.href);
      return u.origin === window.location.origin && u.pathname.startsWith('/api/');
    } catch {
      return false;
    }
  };

  window.fetch = (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    try {
      const url =
        typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
      const isAuthEndpoint = /\/api\/auth\/(login|refresh)/.test(url || '');
      const token = getToken();
      if (token && !isAuthEndpoint && sameOriginApi(url)) {
        const fromRequest =
          typeof input !== 'string' && !(input instanceof URL) ? (input as Request).headers : undefined;
        const headers = new Headers(init?.headers || fromRequest || undefined);
        if (!headers.has('Authorization')) {
          headers.set('Authorization', `Bearer ${token}`);
          init = { ...(init || {}), headers };
        }
      }
    } catch {
      /* fall through to a plain fetch — never break fetch */
    }
    // 401 AUTH_EXPIRED on a gated /api call = the access token lapsed or was rotated by another tab: refresh
    // the session once (cross-tab safe) and replay the request with the new token. Never on auth endpoints.
    let url0 = '';
    try { url0 = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url; } catch { /* ignore */ }
    const replayable = sameOriginApi(url0) && !/\/api\/auth\//.test(url0) && !(typeof input !== 'string' && !(input instanceof URL));
    return orig(input as RequestInfo | URL, init).then(async (res) => {
      // Organisation MFA (owner 2026-09-29): a 403 MFA_* is surfaced once, globally — the layout offers the passkey
      // step-up / enrolment; the protected payload is never shown without it.
      if (res.status === 403 && sameOriginApi(url0)) {
        try {
          const code = (await res.clone().json())?.code || '';
          if (code === 'MFA_CHALLENGE_REQUIRED' || code === 'MFA_ENROLLMENT_REQUIRED') {
            window.dispatchEvent(new CustomEvent('saa-mfa-required', { detail: { code } }));
          }
        } catch { /* not JSON */ }
        return res;
      }
      if (res.status !== 401 || !replayable) return res;
      let code = '';
      try { code = (await res.clone().json())?.code || ''; } catch { /* not JSON */ }
      if (code !== 'AUTH_EXPIRED') return res;
      // Another tab / an earlier call already renewed the token after this request was sent → just replay
      // with the current one; a second exchange would only rotate the session again.
      const sent = new Headers(init?.headers || undefined).get('Authorization') || '';
      const now = getToken();
      if (now && sent && sent !== `Bearer ${now}`) {
        const h = new Headers(init?.headers || undefined);
        h.set('Authorization', `Bearer ${now}`);
        return orig(input as RequestInfo | URL, { ...(init || {}), headers: h });
      }
      const ok = await refreshSession();
      const t = getToken();
      if (!ok || !t) {
        try { window.dispatchEvent(new CustomEvent('saa-auth-expired')); } catch { /* ignore */ }
        return res;
      }
      const headers = new Headers(init?.headers || undefined);
      headers.set('Authorization', `Bearer ${t}`);
      return orig(input as RequestInfo | URL, { ...(init || {}), headers });
    });
  };
}
